import { IGoogleBusinessAccount, IGoogleBusinessConnection, IOperationResultNoValue } from '@ViewModels';
import { css } from 'aphrodite';
import { useState } from 'react';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import {
	invalidateGoogleBusinessConnection,
	invalidateGoogleBusinessMetadata,
	invalidateInfiniteReviews,
	useGetGoogleBusinessConnection,
	usePutGoogleBusinessSyncPreferences,
	useSyncGoogleBusiness,
} from '../../../../queries';
import { Button } from '../../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogClose,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../components-v2/Dialog';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { styleSheet } from './styles';

const formatAccountType = (type: string) => {
	if (!type) {
		return null;
	}

	return type
		.split('_')
		.map(word => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`)
		.join(' ');
};

const describeAccount = (account: IGoogleBusinessAccount) => account.accountName || account.name || 'Unnamed account';

const isSyncing = (sync?: boolean) => sync !== false;

const ConnectedLocations = () => {
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('ConnectedLocations');
	const [pendingSync, setPendingSync] = useState<Record<string, boolean>>({});
	const [isConfirmingDisable, setIsConfirmingDisable] = useState(false);

	const connectionQuery = useGetGoogleBusinessConnection({
		enabled: true,
		meta: { toastErrorMessage: 'Failed to load your Google Business locations' },
	});

	const onMutationError = (operationName: string) => (error: IOperationResultNoValue) => {
		errorMessages.push({ messages: [`Failed to ${operationName}`] });
		logApiError(operationName, error);
	};

	const { mutate: syncGoogleBusiness, isPending: isSyncPending } = useSyncGoogleBusiness({
		onError: onMutationError('refresh your Google Business locations'),
		onSuccess: () => {
			invalidateGoogleBusinessConnection();
			invalidateGoogleBusinessMetadata();
			invalidateInfiniteReviews();
		},
	});

	const { mutate: saveSyncPreferences, isPending: isSavePending } = usePutGoogleBusinessSyncPreferences({
		onError: onMutationError('save your location settings'),
		onSuccess: () => {
			setPendingSync({});
			syncGoogleBusiness();
		},
	});

	if (connectionQuery.isPending) {
		return <LoadingSpinner type='small' />;
	}

	const accounts = connectionQuery.data?.googleBusinessAccounts ?? [];
	const locations = connectionQuery.data?.locations ?? [];

	const syncValueFor = (locationName: string, sync?: boolean) =>
		locationName in pendingSync ? pendingSync[locationName] : isSyncing(sync);

	const changedLocations = locations.filter(location => {
		const next = syncValueFor(location.name, location.sync);
		return next !== isSyncing(location.sync);
	});
	const locationsBeingDisabled = changedLocations.filter(location => !syncValueFor(location.name, location.sync));
	const isBusy = isSavePending || isSyncPending;

	const onToggleLocation = (locationName: string, nextSync: boolean) => {
		setPendingSync(current => ({ ...current, [locationName]: nextSync }));
	};

	const commitSyncPreferences = () => {
		setIsConfirmingDisable(false);
		const connection: IGoogleBusinessConnection = {
			...connectionQuery.data,
			locations: locations.map(location => ({
				...location,
				sync: syncValueFor(location.name, location.sync),
			})),
		};
		saveSyncPreferences(connection);
	};

	const onSaveClicked = () => {
		if (locationsBeingDisabled.length) {
			setIsConfirmingDisable(true);
			return;
		}

		commitSyncPreferences();
	};

	const onRefreshClicked = () => {
		syncGoogleBusiness();
	};

	const refreshButton = (
		<Button variant='outline' size='sm' isLoading={isBusy} onClick={onRefreshClicked}>
			Refresh
		</Button>
	);

	if (!accounts.length) {
		return (
			<div className={`${css(styleSheet.container)} connected-locations`}>
				<div className={css(styleSheet.warning)}>
					<span className={css(styleSheet.warningTitle)}>
						We can&apos;t see any Google Business accounts on the Google login you connected.
					</span>
					<span>
						This usually means the Google account you signed in with doesn&apos;t manage a business listing. Disconnect
						and reconnect using the Google account that manages your business on Google.
					</span>
				</div>
				<div className={css(styleSheet.sectionHeader)}>{refreshButton}</div>
			</div>
		);
	}

	const disabledCount = locations.filter(location => !syncValueFor(location.name, location.sync)).length;

	return (
		<div className={`${css(styleSheet.container)} connected-locations`}>
			<div>
				<h4 className={css(styleSheet.sectionTitle)}>Connected Google account</h4>
				{accounts.map(account => {
					const accountType = formatAccountType(account.type);
					return (
						<div className={css(styleSheet.accountCard)} key={account.name || account.accountName}>
							<span className={css(styleSheet.accountName)}>{describeAccount(account)}</span>
							{accountType ? <span className={css(styleSheet.accountType)}>{accountType}</span> : null}
						</div>
					);
				})}
			</div>

			<div>
				<h4 className={css(styleSheet.sectionTitle)}>
					{locations.length === 1 ? 'Connected location' : `Connected locations (${locations.length})`}
				</h4>

				{locations.length ? (
					<>
						<p className={css(styleSheet.listHint)}>
							Turn off syncing for a location to stop collecting its reviews. Its existing reviews will be hidden from
							your reports until you turn it back on.
						</p>
						<ul className={css(styleSheet.locationList)}>
							{locations.map(location => {
								const willSync = syncValueFor(location.name, location.sync);
								return (
									<li className={css(styleSheet.locationCard)} key={location.name || location.title}>
										<div className={css(styleSheet.locationSummary)}>
											<span className={css(styleSheet.locationTitle)}>{location.title}</span>
											{location.rating ? <span className={css(styleSheet.rating)}>{location.rating} ★</span> : null}
											<span className={css(styleSheet.reviewCount)}>
												{location.reviewCount === 1 ? '1 review' : `${location.reviewCount ?? 0} reviews`}
											</span>
											{!willSync ? <span className={css(styleSheet.syncDisabled)}>Not syncing</span> : null}
										</div>
										<div className={css(styleSheet.locationAction)}>
											<Button
												variant={willSync ? 'destructive-secondary' : 'secondary'}
												size='sm'
												disabled={isBusy}
												aria-label={`${willSync ? 'Disable' : 'Enable'} sync for ${location.title}`}
												onClick={() => onToggleLocation(location.name, !willSync)}
											>
												{willSync ? 'Disable sync' : 'Enable sync'}
											</Button>
										</div>
									</li>
								);
							})}
						</ul>

						{changedLocations.length ? (
							<div className={css(styleSheet.saveBar)}>
								<span className={css(styleSheet.saveBarText)}>
									{changedLocations.length === 1
										? '1 location changed'
										: `${changedLocations.length} locations changed`}
								</span>
								<Button variant='secondary' size='sm' disabled={isBusy} onClick={() => setPendingSync({})}>
									Cancel
								</Button>
								<Button variant='primary' size='sm' isLoading={isBusy} onClick={onSaveClicked}>
									Save changes
								</Button>
							</div>
						) : null}

						{!changedLocations.length && disabledCount === locations.length ? (
							<div className={css(styleSheet.warning)}>
								<span className={css(styleSheet.warningTitle)}>No location is set to sync.</span>
								<span>
									You&apos;ve turned off syncing for every location, so your reviews are hidden from your dashboard and
									reports and no new ones are coming in. Check one back on to start syncing again.
								</span>
							</div>
						) : null}
					</>
				) : (
					<div className={css(styleSheet.warning)}>
						<span className={css(styleSheet.warningTitle)}>
							We couldn&apos;t find any business locations on the Google account you connected.
						</span>
						<span>
							If {describeAccount(accounts[0])} isn&apos;t the right Google account, disconnect and reconnect with the
							account that manages your business listing.
						</span>
						<span>Already fixed this on Google? Check again:</span>
						{refreshButton}
					</div>
				)}
			</div>

			<Dialog open={isConfirmingDisable} onOpenChange={setIsConfirmingDisable}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>
							Stop syncing {locationsBeingDisabled.length === 1 ? 'this location' : 'these locations'}?
						</DialogTitle>
					</DialogHeader>
					<DialogBody>
						<p>
							Reviews already collected from {locationsBeingDisabled.map(location => location.title).join(', ')} will be
							hidden from your dashboard and reports, and no new reviews will come in.
						</p>
						<p>
							Nothing is deleted on Google, and your replies are kept — turning the location back on restores its
							reviews on the next sync.
						</p>
					</DialogBody>
					<DialogFooter>
						<DialogClose render={<Button variant='secondary'>Keep syncing</Button>} />
						<Button variant='destructive' onClick={commitSyncPreferences}>
							Stop syncing
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
		</div>
	);
};

export { ConnectedLocations };
